import { useUser } from 'containers/user/hooks/useUser'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { APP_LINKS, joinWithSlash, linkWithParams } from 'utilities/links'
import { useIsOwnProfile } from 'containers/profile/hooks/useProfile'

const AVATAR_DEFAULT =
  'https://storage.googleapis.com/profilepagev1.appspot.com/avatar.png'
const CHECKLIST_ID = 1933
const CHECKLIST_ID_PROFILE = 'profileComplete'
const CHECKLIST_ID_PROJECTS = 'projectComplete'
const CHECKLIST_ID_SYNC = 'syncComplete'

export const useProfileOnboardingChecklist = () => {
  const { user, isLoading } = useUser()
  const ownProfile = useIsOwnProfile()
  const router = useRouter()
  const productFruitsIsReady = window.productFruitsIsReady

  const initListner = useCallback(
    (profileComplete, projectComplete, syncComplete) => {
      // listen for checklist clicks and check status
      window.productFruits.api?.checklists.listen(
        'item-launched',
        (id, internalId) => {
          switch (internalId) {
            case CHECKLIST_ID_PROFILE:
              if (profileComplete) {
                window.productFruits.api?.checklists.markItemAsDone(internalId)
              } else {
                // launch profile editor
                router.push(
                  joinWithSlash(user?.usernameLower, APP_LINKS.PROFILE_EDIT)
                )
              }
              break

            case CHECKLIST_ID_SYNC:
              if (syncComplete) {
                window.productFruits.api?.checklists.markItemAsDone(internalId)
              } else {
                router.push(
                  linkWithParams(APP_LINKS.SETTINGS, {
                    section: 'socialMediaConnections',
                  })
                )
              }
              break

            case CHECKLIST_ID_PROJECTS:
              if (projectComplete) {
                window.productFruits.api?.checklists.markItemAsDone(internalId)
              } else {
                router.push(
                  joinWithSlash(user?.usernameLower, APP_LINKS.PROFILE_PROJECTS)
                )
              }
              break
          }
        }
      )
    },
    [router, user]
  )

  useEffect(() => {
    if (!productFruitsIsReady) return

    if (isLoading || !ownProfile) return

    const checklist = window.productFruits.api.checklists
      .getChecklists()
      .find((checklist) => checklist.id === CHECKLIST_ID)

    if (!checklist) return

    const pFDiv = document.getElementById('profile-onboarding-checklist')
    window.productFruits.api.checklists.injectToElement(CHECKLIST_ID, pFDiv)

    // Issue #1589 below are not initialized on user creation
    const checkUserAboutType =
      Object.hasOwn(user, 'about') && user?.about.length > 0
    const checkUserContentType =
      Object.hasOwn(user, 'contentType') && user?.contentType.length > 0
    const checkUserCreatorType =
      Object.hasOwn(user, 'creatorType') && user?.creatorType.length > 0
    const checkUserCollabInterest =
      Object.hasOwn(user, 'collabInterest') && user?.collabInterest.length > 0

    const profileComplete =
      user?.firstName &&
      user?.lastName &&
      user?.avatar !== AVATAR_DEFAULT &&
      user?.avatar !== null &&
      checkUserAboutType &&
      checkUserContentType &&
      checkUserCreatorType &&
      checkUserCollabInterest
    const projectComplete = user?.projects?.length > 0
    const syncComplete =
      user?.connections?.length > 0 || user?.connectionsSent?.length > 0

    // attempt to check items off for existing users
    if (checklist.state !== 'finished') {
      const items = checklist.items
      const profileCompleteItem = items.find(
        (item) => item.internalId === CHECKLIST_ID_PROFILE
      )
      const projectCompleteItem = items.find(
        (item) => item.internalId === CHECKLIST_ID_PROJECTS
      )
      const syncCompleteItem = items.find(
        (item) => item.internalId === CHECKLIST_ID_SYNC
      )

      if (profileComplete && profileCompleteItem.state !== 'done') {
        window.productFruits.api.checklists.markItemAsDone(CHECKLIST_ID_PROFILE)
      }

      if (projectComplete && projectCompleteItem.state !== 'done') {
        window.productFruits.api.checklists.markItemAsDone(
          CHECKLIST_ID_PROJECTS
        )
      }

      if (syncComplete && syncCompleteItem.state !== 'done') {
        window.productFruits.api.checklists.markItemAsDone(CHECKLIST_ID_SYNC)
      }
    }

    // listen for checklist clicks
    initListner(profileComplete, projectComplete, syncComplete)

    // Cleanup function
    return () => {
      if (pFDiv) {
        pFDiv.innerHTML = '' // Remove the injected checklist
      }
    }
  }, [user, ownProfile, router, productFruitsIsReady, isLoading, initListner])
}
